import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import SocialButtons from "../components/SocialButtons";
import { useAuth } from "@/hooks/useAuth";

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password.trim()) {
      setError("Please enter your email and password.");
      return;
    }
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back."
      subtitle="Sign in to manage your bookings, message your cleaner, and lock in your favorite weekly slot."
      footerText="New to PureNest?"
      footerLinkLabel="Create an account"
      footerLinkTo="/signup"
      imageUrl="https://readdy.ai/api/search-image?query=Serene%20sunlit%20bedroom%20interior%20with%20crisp%20white%20linen%20bedding%20cream%20walls%20wooden%20floor%20and%20olive%20branch%20in%20ceramic%20vase%20on%20nightstand%2C%20soft%20golden%20morning%20light%20streaming%20through%20sheer%20curtains%2C%20editorial%20interior%20photography%20warm%20neutral%20palette%20calm%20peaceful%20premium%20home%20atmosphere&width=1400&height=1800&seq=auth-login-2026&orientation=portrait"
      imageAlt="Sunlit bedroom"
      quote="Five minutes to book, three hours later my whole apartment felt brand new. PureNest is the only subscription I'd never cancel."
      quoteAuthor="Eleanor Whitcombe"
    >
      <SocialButtons />

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-background-200"></div>
        <span className="text-xs text-foreground-600 uppercase tracking-widest">or</span>
        <div className="flex-1 h-px bg-background-200"></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="auth-login-form">
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            Email
          </label>
          <div className="relative">
            <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-600"></i>
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full pl-11 pr-4 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="block text-sm font-medium text-foreground-800">
              Password
            </label>
            <a href="#" className="text-xs text-primary-600 hover:text-primary-700 font-medium">
              Forgot password?
            </a>
          </div>
          <div className="relative">
            <i className="ri-lock-2-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-600"></i>
            <input
              id="login-password"
              name="password"
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full pl-11 pr-12 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground-600 hover:text-foreground-900 cursor-pointer"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              <i className={showPw ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2.5 text-sm text-foreground-800 cursor-pointer select-none">
          <span
            onClick={() => setRemember((v) => !v)}
            className={`w-5 h-5 flex items-center justify-center rounded-md border transition-colors ${
              remember
                ? "bg-primary-500 border-primary-500 text-background-50"
                : "border-background-300 bg-background-50"
            }`}
          >
            {remember && <i className="ri-check-line text-sm"></i>}
          </span>
          Keep me signed in on this device
        </label>

        {error && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-accent-100 text-accent-900 text-sm">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="group w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Signing you in…
            </>
          ) : (
            <>
              Sign in
              <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
            </>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}